// Firmenangaben und Logo — das, was auf jeder Rechnung erscheint.
import { useEffect, useState } from 'react';
import { Erfolgsmeldung } from '../../../gemeinsam/komponenten/Meldungen';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { Feld, Hinweis, Karte, KartenKopf, Knopf, Spinner } from '../../../gemeinsam/komponenten/Basis';
import { useSprache } from '../../../gemeinsam/lib/i18n';

type Firma = {
  name: string; strasse: string; plz: string; ort: string; land: string;
  telefon: string; email: string; web: string; ustId: string; steuernummer: string;
  iban: string; bic: string; bank: string; geschaeftsfuehrer: string;
  handelsregister: string; fusstext: string; logo: string | null;
  /** Zahlcode auf der Rechnung — standardmäßig an. */
  girocode: boolean;
};

// Hochladen bis 5 MB; der Server verkleinert aufs Belegformat (900 × 360 Pixel).
const MAX_KB = 5 * 1024;

export default function Firma() {
  const { t } = useSprache();
  const [firma, setFirma] = useState<Firma | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);

  useEffect(() => {
    void (async () => {
      try { setFirma(await api<Firma>('/firma')); }
      catch (e) { setFehler(e instanceof ApiFehler ? e.message : 'Firmenangaben konnten nicht geladen werden.'); }
    })();
  }, []);

  async function speichern(e: React.FormEvent) {
    e.preventDefault();
    if (!firma) return;
    setSendet(true);
    setFehler(null);
    try {
      const { logo, ...angaben } = firma;
      await api('/firma', { methode: 'PUT', daten: angaben });
      setMeldung('Firmenangaben gespeichert. Sie erscheinen ab jetzt auf jeder Rechnung.');
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSendet(false);
    }
  }

  async function logoWaehlen(e: React.ChangeEvent<HTMLInputElement>) {
    const datei = e.target.files?.[0];
    if (!datei) return;
    setFehler(null);
    setMeldung(null);

    if (datei.size > MAX_KB * 1024) {
      setFehler(`Das Bild ist ${(datei.size / 1024 / 1024).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB groß. Erlaubt sind 5 MB.`);
      return;
    }

    // Als Data-URL einlesen — das Backend legt sie direkt in den Einstellungen ab.
    const leser = new FileReader();
    leser.onload = async () => {
      try {
        const ergebnis = await api<{ logo: string; bytes: number; vorher: number; verkleinert: boolean }>('/firma/logo', {
          methode: 'PUT', daten: { logo: String(leser.result) },
        });
        setFirma(f => f && ({ ...f, logo: ergebnis.logo }));
        const kb = (n: number) => `${Math.max(1, Math.round(n / 1024)).toLocaleString('de-DE')} KB`;
        setMeldung(ergebnis.verkleinert
          ? `Logo gespeichert — fürs Belegformat verkleinert von ${kb(ergebnis.vorher)} auf ${kb(ergebnis.bytes)}.`
          : 'Logo gespeichert.');
      } catch (err) {
        setFehler(err instanceof ApiFehler ? err.message : 'Logo konnte nicht gespeichert werden.');
      }
    };
    leser.onerror = () => setFehler('Datei konnte nicht gelesen werden.');
    leser.readAsDataURL(datei);
  }

  async function logoEntfernen() {
    try {
      await api('/firma/logo', { methode: 'PUT', daten: { logo: null } });
      setFirma(f => f && ({ ...f, logo: null }));
      setMeldung('Logo entfernt.');
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Entfernen fehlgeschlagen.');
    }
  }

  if (!firma) {
    return fehler ? <Hinweis art="fehler">{fehler}</Hinweis>
      : <div className="flex justify-center py-16"><Spinner /></div>;
  }

  const feld = (name: keyof Firma, label: string, extra: Record<string, unknown> = {}) => (
    <Feld
      label={label}
      value={String(firma[name] ?? '')}
      onChange={e => setFirma(f => f && ({ ...f, [name]: e.target.value }))}
      {...extra}
    />
  );

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{t('firma.titel')}</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Diese Angaben stehen auf Ihren Rechnungen
        </p>
      </div>

      {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
      <Erfolgsmeldung text={meldung} />

      <Karte>
        <KartenKopf titel={t('firma.logo')} hinweis="PNG, JPEG, WebP oder SVG · bis 5 MB · wird automatisch fürs Belegformat verkleinert" />
        <div className="flex flex-wrap items-center gap-5 p-5">
          <div className="flex h-24 w-40 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 dark:border-slate-700 dark:bg-slate-950">
            {firma.logo
              ? <img src={firma.logo} alt="Firmenlogo" className="max-h-20 max-w-36 object-contain" />
              : <span className="text-xs text-slate-400 dark:text-slate-500">kein Logo</span>}
          </div>
          <div className="space-y-2">
            <label className="inline-block">
              <span className="sr-only">{t('firma.logoWaehlen')}</span>
              <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml"
                     onChange={logoWaehlen}
                     className="block text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-marke-600 file:px-3.5 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-marke-700 dark:text-slate-400" />
            </label>
            {firma.logo && (
              <Knopf variante="still" className="!text-xs" onClick={logoEntfernen}>{t('firma.logoEntfernen')}</Knopf>
            )}
          </div>
        </div>
      </Karte>

      <form onSubmit={speichern} className="space-y-4">
        <Karte>
          <KartenKopf titel={t('firma.anschrift')} />
          <div className="grid gap-4 p-5 sm:grid-cols-2">
            {feld('name', 'Firmenname', { required: true })}
            {feld('strasse', 'Straße und Hausnummer')}
            <div className="grid grid-cols-[100px_1fr] gap-3">
              {feld('plz', 'PLZ')}
              {feld('ort', 'Ort')}
            </div>
            {feld('land', 'Land')}
            {feld('telefon', 'Telefon')}
            {feld('email', 'E-Mail', { type: 'email' })}
            {feld('web', 'Webseite')}
          </div>
        </Karte>

        <Karte>
          <KartenKopf titel={t('firma.steuerRegister')} hinweis={t('firma.pflichtangaben')} />
          <div className="grid gap-4 p-5 sm:grid-cols-2">
            {feld('ustId', 'USt-IdNr.')}
            {feld('steuernummer', 'Steuernummer')}
            {feld('geschaeftsfuehrer', 'Geschäftsführung')}
            {feld('handelsregister', 'Handelsregister')}
          </div>
        </Karte>

        <Karte>
          <KartenKopf titel={t('firma.bank')} hinweis={t('firma.imFuss')} />
          <div className="grid gap-4 p-5 sm:grid-cols-2">
            {feld('bank', 'Bank')}
            {feld('iban', 'IBAN')}
            {feld('bic', 'BIC')}
          </div>

          <div className="border-t border-slate-200 p-5 dark:border-slate-800">
            <label className="flex cursor-pointer items-start gap-3">
              <input type="checkbox" checked={firma.girocode !== false}
                     onChange={e => setFirma({ ...firma, girocode: e.target.checked })}
                     className="mt-0.5 h-4 w-4 rounded border-slate-300 text-marke-600" />
              <span className="text-sm text-slate-700 dark:text-slate-300">
                {t('firma.girocode')}
                <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                  {t('firma.girocodeText')}
                </span>
              </span>
            </label>
            {firma.girocode !== false && !ibanPlausibel(firma.iban) && (
              <p className="mt-3 text-xs text-amber-700 dark:text-amber-400">
                {firma.iban.trim() ? t('firma.ibanUngueltig') : t('firma.ibanFehlt')}
              </p>
            )}
          </div>
        </Karte>

        <Karte>
          <KartenKopf titel={t('firma.fusstext')} hinweis={t('firma.aufRechnung')} />
          <div className="p-5">
            {feld('fusstext', 'Fußtext')}
          </div>
        </Karte>

        <Knopf type="submit" variante="primaer" laedt={sendet}>{t('firma.speichern')}</Knopf>
      </form>
    </div>
  );
}

/**
 * IBAN-Prüfsumme nach ISO 13616 — dieselbe Rechnung wie auf dem Server.
 *
 * Hier nur, um sofort zu warnen: wer die IBAN vertippt, soll es beim Eintippen
 * merken und nicht erst, wenn die Rechnung ohne Zahlcode herauskommt.
 */
function ibanPlausibel(roh: string): boolean {
  const iban = String(roh || '').replace(/[\s-]/g, '').toUpperCase();
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{10,30}$/.test(iban)) return false;
  const umgestellt = iban.slice(4) + iban.slice(0, 4);
  let rest = 0;
  for (const zeichen of umgestellt) {
    const wert = zeichen >= 'A' && zeichen <= 'Z' ? String(zeichen.charCodeAt(0) - 55) : zeichen;
    for (const ziffer of wert) rest = (rest * 10 + Number(ziffer)) % 97;
  }
  return rest === 1;
}
